import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { IMAGES, STATS } from "@/data/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = ({ onContact }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const orbY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-20"
    >
      {/* Background image with mask */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-20">
        <img src={IMAGES.heroBg} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-[#0A1428]/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1428] via-[#0A1428]/40 to-[#0A1428]/70" />
      </motion.div>

      {/* Glowing orb */}
      <motion.div
        style={{ y: orbY }}
        className="hero-orb absolute right-[-10%] top-[15%] w-[55vw] h-[55vw] max-w-[720px] max-h-[720px] -z-10 rounded-full"
      />

      <motion.div
        style={{ y: textY }}
        className="max-w-7xl mx-auto px-5 md:px-8 w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 mb-8"
          data-testid="hero-badge"
        >
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={13} className="fill-[#1E6BFF] text-[#1E6BFF]" />
            ))}
          </div>
          <span className="text-xs text-gray-300 font-medium">4.9 · Trusted by 120+ small businesses</span>
        </motion.div>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
          {["Websites That Turn", "Local Searches Into", "New Customers."].map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className={`inline-block ${i === 2 ? "text-[#1E6BFF]" : ""}`}
              >
                {t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="mt-8 max-w-xl text-lg md:text-xl text-gray-300 leading-relaxed"
        >
          Titan Web Solutions builds professional, mobile-friendly websites for service
          businesses in Nashville and across Middle Tennessee. From clearer service pages to
          quote-request forms and conversion tracking, we help make it easier for customers
          to choose your business and get in touch.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            onClick={onContact}
            data-testid="hero-plan-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white font-semibold px-8 py-4 text-base transition-colors duration-200 glow-blue"
          >
            Get a Free Website Plan
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => scrollTo("#work")}
            data-testid="hero-work-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/50 text-white font-semibold px-8 py-4 text-base transition-colors duration-200"
          >
            View Our Work
          </button>
        </motion.div>

        {/* Inline stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-2xl border-t border-white/10 pt-8"
        >
          {STATS.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label}`}>
              <div className="font-display font-bold text-2xl md:text-3xl text-white">{s.value}</div>
              <div className="text-xs md:text-sm text-gray-400 mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
